import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { FBXLoader } from "three/examples/jsm/loaders/FBXLoader.js";
import { TGALoader } from "three/examples/jsm/loaders/TGALoader.js";
import * as SkeletonUtils from "three/examples/jsm/utils/SkeletonUtils.js";
import { ResourceID } from "./resource_id.js";
import { error } from "../utils/logger.js";
import { Vfx3DGlobalAtlas } from "../vfx_3d/core/textures/vfx_3d_global_atlas.js";
import { VFX_3D_ATLAS_CONFIG, VFX_3D_ATLAS_SIZE } from "../vfx_3d/vfx_3d_atlas_config.js";
import { GlobalSettings } from "../global_settings.js";
import { buildAndRegisterGlassShards } from "../vfx_3d/effects/jam/glass_shard_texture_builder.js";

class EmptyFbxTextureLoader extends THREE.Loader<THREE.DataTexture>
{
  public load(
    _url: string,
    onLoad?: ( texture: THREE.DataTexture ) => void
  ): THREE.DataTexture
  {
    const texture = new THREE.DataTexture(
      new Uint8Array( [255, 255, 255, 0] ),
      1,
      1,
      THREE.RGBAFormat
    );
    texture.needsUpdate = true;
    onLoad?.( texture );
    return texture;
  }
}

const fbx_loading_manager = new THREE.LoadingManager();
// Entity classes replace every FBX material with a game material. Skip stale
// texture references embedded in the source FBX files without HTTP requests.
fbx_loading_manager.addHandler( /\.png$/i, new EmptyFbxTextureLoader( fbx_loading_manager ) );

export class ResourceManager 
{
  private static models: Map<ResourceID, THREE.Object3D> = new Map();
  private static textures: Map<ResourceID, THREE.Texture> = new Map();
  private static animations: Map<ResourceID, THREE.AnimationClip[]> = new Map();

  private static gltf_loader = new GLTFLoader();
  private static fbx_loader = new FBXLoader( fbx_loading_manager );
  private static tex_loader = new THREE.TextureLoader();
  private static tga_loader = new TGALoader();
  private static file_loader = new THREE.FileLoader();

  private static resource_bytes = new Map<string, number>();
  private static downloaded_bytes = 0;

  private static total_to_load = 0;
  private static loaded = 0;

  static onProgress: ( ( progress: number ) => void ) | null = null;

  public static is_loading_finished: boolean = true;

  private static model_queue: { name: ResourceID; path: string; fbx_space: boolean }[] = [];
  private static texture_queue: { name: ResourceID; path: string; srgb: boolean; flip_y: boolean }[] = [];

  public static addModel( name: ResourceID, path: string )
  {
    this.model_queue.push( { name, path, fbx_space: false } );
  }

  /**
   * GLB re-export of art that entity code was tuned against as FBX. Blender
   * writes metres where the FBX had centimetres, and glTF stores V flipped, so
   * the loaded scene is brought back to the FBX scale and UV layout.
   */
  public static addFbxSpaceModel( name: ResourceID, path: string )
  {
    this.model_queue.push( { name, path, fbx_space: true } );
  }

  public static addTexture(
    name: ResourceID,
    path: string,
    srgb: boolean = true,
    flip_y: boolean = false
  )
  {
    this.texture_queue.push( { name, path, srgb, flip_y } );
  }

  public static addNormalMap( name: ResourceID, path: string, flip_y: boolean = true )
  {
    if ( !GlobalSettings.USE_NORMAL_MAPS )
      return;

    this.addTexture( name, path, false, flip_y );
  }

  private static load_promise: Promise<void> | null = null;
  private static boosted = false;

  public static startLoading(): Promise<void>
  {
    if ( !this.load_promise )
      this.load_promise = this.loadAll( 8 );

    return this.load_promise;
  }

  public static whenReady(): Promise<void>
  {
    this.boosted = true;
    return this.startLoading();
  }

  public static pacingDelay(): Promise<void>
  {
    if ( this.boosted )
      return new Promise( resolve => setTimeout( resolve, 0 ) );

    return new Promise( resolve =>
    {
      const ric = ( globalThis as any ).requestIdleCallback;
      if ( ric )
        ric( () => resolve(), { timeout: 500 } );
      else
        setTimeout( resolve, 48 );
    } );
  }

  public static async loadAll( batch_size: number = 0 ): Promise<void>
  {
    this.file_loader.setResponseType( "blob" );

    this.total_to_load = this.model_queue.length + this.texture_queue.length;
    this.loaded = 0;
    this.downloaded_bytes = 0;
    this.resource_bytes.clear();

    const tasks: ( () => Promise<any> )[] = [];

    for ( const m of this.model_queue )
      tasks.push( () => this.loadModel( m.name, m.path, m.fbx_space ) );

    for ( const t of this.texture_queue )
      tasks.push( () => this.loadTexture( t.name, t.path, t.srgb, t.flip_y ) );

    if ( batch_size <= 0 )
    {
      await Promise.all( tasks.map( task => task() ) );
    }
    else
    {
      let i = 0;
      while ( i < tasks.length )
      {
        const width = this.boosted ? batch_size : 1;
        await Promise.all( tasks.slice( i, i + width ).map( task => task() ) );
        i += width;
        await this.pacingDelay();
      }
    }

    buildAndRegisterGlassShards();
    Vfx3DGlobalAtlas.init( VFX_3D_ATLAS_CONFIG, VFX_3D_ATLAS_SIZE );

    this.is_loading_finished = true;
  }

  /**
   * The FBX files keep Blender's Z-up mesh data and put the centimetre scale
   * and the -90° X turn on each mesh node, and entity code reads that local
   * data (the cannon morph, the 2x2 UV projection). Rebuild the same layout:
   * geometry back to Z-up, the turn and scale onto the node.
   */
  private static toFbxSpace( root: THREE.Object3D ): void
  {
    const FBX_UNITS_PER_METRE = 100;
    const z_up = new THREE.Matrix4().makeRotationX( Math.PI / 2 );
    const node_turn = new THREE.Matrix4()
      .makeRotationX( -Math.PI / 2 )
      .scale( new THREE.Vector3( FBX_UNITS_PER_METRE, FBX_UNITS_PER_METRE, FBX_UNITS_PER_METRE ) );
    const converted = new Map<THREE.BufferGeometry, THREE.BufferGeometry>();

    root.traverse( obj =>
    {
      obj.position.multiplyScalar( FBX_UNITS_PER_METRE );

      if ( !( obj instanceof THREE.Mesh ) )
        return;

      obj.updateMatrix();
      obj.matrix.multiply( node_turn ).decompose( obj.position, obj.quaternion, obj.scale );

      const source = obj.geometry as THREE.BufferGeometry;
      const ready = converted.get( source );
      if ( ready )
      {
        obj.geometry = ready;
        return;
      }

      // The FBX morphs carry positions only, and Cannon appends its own
      // position morph; a shorter normal list would break the renderer.
      delete source.morphAttributes.normal;

      // FBX geometry is non-indexed, and toNonIndexed() (which Cannon's
      // toCreasedNormals would call) drops the morph names it looks up.
      const has_morphs = ( source.morphAttributes.position?.length ?? 0 ) > 0;
      const geometry = has_morphs ? source.toNonIndexed() : source;
      converted.set( source, geometry );
      obj.geometry = geometry;

      geometry.applyMatrix4( z_up );

      // applyMatrix4 leaves morph targets alone (the cannon's "fair").
      for ( const morph of geometry.morphAttributes.position ?? [] )
      {
        morph.applyMatrix4( z_up );
        morph.needsUpdate = true;
      }

      // GLTFLoader names targets on the mesh only; updateMorphTargets() reads
      // them from the attributes, where FBXLoader puts them.
      for ( const [ morph_name, index ] of Object.entries( obj.morphTargetDictionary ?? {} ) )
      {
        const morph = geometry.morphAttributes.position?.[index];
        if ( morph )
          morph.name = morph_name;
      }

      geometry.computeBoundingBox();
      geometry.computeBoundingSphere();

      const uv = geometry.getAttribute( "uv" );
      if ( !uv )
        return;

      for ( let i = 0; i < uv.count; i++ )
        uv.setY( i, 1 - uv.getY( i ) );

      uv.needsUpdate = true;
    } );
  }

  private static async loadModel( name: ResourceID, path: string, fbx_space: boolean )
  {
    const clean_path = path.split( "?" )[0].toLowerCase();

    if ( clean_path.endsWith( ".fbx" ) )
    {
      return new Promise<void>( resolve =>
      {
        this.fbx_loader.load(
          path,
          model =>
          {
            this.models.set( name, model );
            this.animations.set( name, model.animations ?? [] );
            this.updateProgress();
            resolve();
          },
          xhr =>
          {
            const prev = this.resource_bytes.get( path ) ?? 0;
            const delta = xhr.loaded - prev;

            if ( delta > 0 )
            {
              this.downloaded_bytes += delta;
              this.resource_bytes.set( path, xhr.loaded );
            }
          },
          err =>
          {
            error( "Failed to load model", path, err );
            this.updateProgress();
            resolve();
          }
        );
      } );
    }

    return new Promise<void>( resolve =>
    {
      this.gltf_loader.load(
        path,
        gltf =>
        {
          if ( fbx_space )
            this.toFbxSpace( gltf.scene );

          this.models.set( name, gltf.scene );
          this.animations.set( name, gltf.animations ?? [] );

          this.updateProgress();
          resolve();
        },
        xhr =>
        {
          const prev = this.resource_bytes.get( path ) ?? 0;
          const delta = xhr.loaded - prev;

          if ( delta > 0 )
          {
            this.downloaded_bytes += delta;
            this.resource_bytes.set( path, xhr.loaded );
          }
        },
        err =>
        {
          error( "Failed to load model", path, err );
          this.updateProgress();
          resolve();
        }
      );
    } );
  }

  private static async loadTexture(
    name: ResourceID,
    path: string,
    srgb: boolean,
    flip_y: boolean
  )
  {
    return new Promise<void>( resolve =>
    {
      this.file_loader.load(
        path,

        blob =>
        {
          const url = URL.createObjectURL( blob as any );

          this.tex_loader.load(
            url,
            tex =>
            {
              URL.revokeObjectURL( url );

              tex.flipY = flip_y;
              tex.colorSpace = srgb
                ? THREE.SRGBColorSpace
                : THREE.NoColorSpace;

              this.textures.set( name, tex );

              this.resource_bytes.delete( path );

              this.updateProgress();
              resolve();
            },
            undefined,
            () =>
            {
              URL.revokeObjectURL( url );

              this.updateProgress();
              resolve();
            }
          );
        },

        xhr =>
        {
          const prev = this.resource_bytes.get( path ) ?? 0;
          const delta = xhr.loaded - prev;

          if ( delta > 0 )
          {
            this.downloaded_bytes += delta;
            this.resource_bytes.set( path, xhr.loaded );
          }
        },

        () =>
        {
          this.updateProgress();
          resolve();
        }
      );
    } );
  }

  public static getDownloadedBytes(): number
  {
    return this.downloaded_bytes;
  }

  public static getDownloadedCount(): number
  {
    return this.loaded;
  }

  public static getTotalCount(): number
  {
    return this.total_to_load;
  }

  private static updateProgress()
  {
    this.loaded++;
    if ( this.onProgress )
      this.onProgress( this.loaded / this.total_to_load );
  }

  public static getModel( name: ResourceID ): THREE.Object3D
  {
    return SkeletonUtils.clone( this.models.get( name )! );
  }

  public static getAnimations( name: ResourceID ): THREE.AnimationClip[]
  {
    return this.animations.get( name )!;
  }

  public static getTexture( name: ResourceID ): THREE.Texture
  {
    return this.textures.get( name )!;
  }

  public static setTexture( name: ResourceID, texture: THREE.Texture ): void
  {
    this.textures.set( name, texture );
  }

  // Returns null while normal maps are off, so a material never ends up with an
  // undefined map because its texture was skipped at load time.
  public static getNormalMap( name: ResourceID ): THREE.Texture | null
  {
    if ( !GlobalSettings.USE_NORMAL_MAPS )
      return null;

    return this.textures.get( name ) ?? null;
  }

  public static dispose()
  {
    this.models.forEach( obj =>
    {
      obj.traverse( c =>
      {
        if ( ( c as any ).geometry ) ( c as any ).geometry.dispose();
        if ( ( c as any ).material )
        {
          const m = ( c as any ).material;
          Array.isArray( m ) ? m.forEach( x => x.dispose() ) : m.dispose();
        }
      } );
    } );

    this.textures.forEach( t => t.dispose() );

    this.models.clear();
    this.textures.clear();
    this.animations.clear();
  }
}
